import React, { useState, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { toast } from "sonner";
import axios from "axios";
import { getAuthHeaders } from "../lib/authHeaders";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "./ui/button";
import {
  Download, MessageCircle, FolderPlus, User, Search,
  Printer, Copy, Share2, FileText, ChevronUp, ChevronDown,
  AlertTriangle, Pill, Calendar, Activity, DollarSign, ShieldAlert
} from "lucide-react";

const BACKEND_URL = "";
const API = `${BACKEND_URL}/api`;

class ProtocolErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error("ProtocolView crashed:", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="max-w-4xl mx-auto p-6 text-center" data-testid="protocol-error-boundary">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-700 mb-2">Something went wrong</h2>
          <p className="text-gray-500 mb-4">The protocol display encountered an error. Your protocol data is safe.</p>
          <Button onClick={() => this.setState({ hasError: false })} className="bg-teal-600 hover:bg-teal-700 text-white mr-2">Retry</Button>
          <Button variant="outline" onClick={() => this.props.setCurrentView('assessment')}>New Assessment</Button>
        </div>
      );
    }
    return this.props.children;
  }
}

/* ──────── At-a-Glance Data Extractor ──────── */
function extractAtAGlance(protocol, assessment) {
  const peptides = [];
  const pp = protocol.primary_peptides || [];
  const rp = protocol.recommended_peptides || [];
  const dosing = protocol.detailed_dosing_protocols?.standard_dosing || {};
  const perCosts = protocol.cost_estimates?.per_peptide_costs || protocol.cost_estimates?.per_peptide || {};

  const seen = new Set();
  [...pp, ...rp].forEach(p => {
    const name = p.name || p;
    if (seen.has(name)) return;
    seen.add(name);
    const d = dosing[name] || {};
    peptides.push({
      name,
      dose: p.dosage || p.personalized_dosing || d.dose || d.starting_dose || '',
      route: p.route || d.route || '',
      frequency: p.frequency || d.frequency || '',
      cost: perCosts[name] || ''
    });
  });

  const di = protocol.comprehensive_contraindications?.drug_interactions || protocol.drug_interactions || [];
  const interactionCount = Array.isArray(di) ? di.length : 0;

  const outcomes = protocol.expected_outcomes_statistics || {};
  const timeline = protocol.expected_timeline || {};
  const totalCost = protocol.cost_estimates?.total_monthly_estimate || protocol.cost_estimates?.total_monthly || '';

  return {
    patient: {
      name: assessment?.patient_name || 'Patient',
      age: assessment?.age || '',
      sex: assessment?.gender || '',
      weight: assessment?.weight ? `${assessment.weight} lbs` : '',
      height: (assessment?.height_feet && assessment?.height_inches) ? `${assessment.height_feet}'${assessment.height_inches}"` : ''
    },
    concerns: assessment?.primary_concerns || [],
    peptides,
    interactionCount,
    interactions: di,
    outcomes,
    timeline,
    totalCost,
    successRate: outcomes.success_rate || ''
  };
}

/* ──────── Section Splitter ──────── */
function splitIntoSections(markdown) {
  if (!markdown) return [];
  const lines = markdown.split('\n');
  const sections = [];
  let current = null;

  for (const line of lines) {
    const match = line.match(/^#{1,3}\s+(?:SECTION\s*)?(\d+)?[.:)]*\s*(.*)/i) || line.match(/^#{1,3}\s+(.*)/);
    if (match) {
      if (current) sections.push(current);
      current = { title: (match[2] || match[1] || '').trim(), content: line + '\n' };
    } else {
      if (current) current.content += line + '\n';
      else { if (!sections.length) sections.push({ title: 'Overview', content: '' }); sections[0].content += line + '\n'; }
    }
  }
  if (current) sections.push(current);
  return sections;
}

/* ──────── Dosing Calendar ──────── */
const COLORS = ['#0d9488', '#2563eb', '#7c3aed', '#dc2626', '#ea580c', '#0891b2', '#4f46e5', '#059669'];

function DosingCalendar({ peptides, clinicalReasoning }) {
  const calendarData = useMemo(() => {
    return peptides.map((p, i) => {
      const name = p.name || '';
      const isOral = /oral|capsule|cap/i.test(p.route) || /m-51|rg-5555|n-5550/i.test(name);
      const isDaily = /daily|every day|1x.*day|once daily/i.test(p.frequency) || isOral;
      const isTwiceWeek = /2x.*week|twice.*week|2.*times.*week/i.test(p.frequency);
      const isWeekly = /weekly|once.*week|1x.*week/i.test(p.frequency) && !isTwiceWeek;

      // Parse duration from reasoning
      let weeks = 12;
      const durMatch = clinicalReasoning?.match(new RegExp(`${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^.]*?(\\d+)\\s*weeks?`, 'i'));
      if (durMatch) weeks = Math.min(parseInt(durMatch[1]), 12);

      // Parse escalation from reasoning
      let escalation = null;
      const escMatch = clinicalReasoning?.match(new RegExp(`${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}[^.]*?(?:week|wk)\\s*(\\d+).*?(?:increase|escalat|advance|titrat)[^.]*?(\\d+(?:\\.\\d+)?\\s*(?:mg|mcg))`, 'i'));
      if (escMatch) escalation = { week: parseInt(escMatch[1]), dose: escMatch[2] };

      return { name, dose: p.dose, route: isOral ? 'oral' : (p.route || 'SC'), color: COLORS[i % COLORS.length], weeks, isOral, isDaily, isTwiceWeek, isWeekly, escalation };
    });
  }, [peptides, clinicalReasoning]);

  if (!calendarData.length) return null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 my-4" data-testid="dosing-calendar">
      <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center gap-2">
        <Calendar className="h-4 w-4 text-teal-600" /> 90-Day Dosing Timeline
      </h3>
      <div className="overflow-x-auto">
        <div className="min-w-[600px]">
          {/* Week headers */}
          <div className="flex items-center mb-1">
            <div className="w-40 text-[10px] text-gray-400 font-medium shrink-0">Peptide</div>
            <div className="flex-1 flex">
              {[1,2,3,4,5,6,7,8,9,10,11,12].map(w => (
                <div key={w} className="flex-1 text-center text-[10px] text-gray-400">W{w}</div>
              ))}
            </div>
          </div>
          {/* Peptide rows */}
          {calendarData.map((p, idx) => (
            <div key={idx} className="flex items-center mb-1.5 group" data-testid={`dosing-row-${idx}`}>
              <div className="w-40 shrink-0 pr-2">
                <div className="text-xs font-semibold truncate" style={{ color: p.color }}>{p.name}</div>
                <div className="text-[10px] text-gray-400">{p.dose} {p.route}</div>
              </div>
              <div className="flex-1 flex items-center relative h-6">
                {[1,2,3,4,5,6,7,8,9,10,11,12].map(w => {
                  const active = w <= p.weeks;
                  const hasEscalation = p.escalation && w === p.escalation.week;
                  return (
                    <div key={w} className="flex-1 flex items-center justify-center relative">
                      {active && (
                        <div className="w-full h-3 rounded-sm mx-px relative" style={{ backgroundColor: p.color + '22' }}>
                          {p.isDaily && <div className="absolute inset-0 rounded-sm" style={{ backgroundColor: p.color + '55' }} />}
                          {(p.isWeekly || p.isTwiceWeek) && (
                            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 flex items-center gap-1">
                              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: p.color }} />
                              {p.isTwiceWeek && <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: p.color }} />}
                            </div>
                          )}
                        </div>
                      )}
                      {hasEscalation && (
                        <div className="absolute -top-3 text-[8px] font-bold whitespace-nowrap" style={{ color: p.color }}>
                          &uarr;{p.escalation.dose}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {/* Legend */}
          <div className="flex gap-4 mt-2 text-[10px] text-gray-400 border-t pt-2">
            <span className="flex items-center gap-1"><div className="w-4 h-2 rounded-sm bg-teal-200" /> Daily oral</span>
            <span className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-teal-600" /> Injection day</span>
            <span className="flex items-center gap-1"><div className="w-4 h-2 rounded-sm bg-teal-50 border" /> Active period</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ──────── Collapsible Section ──────── */
function CollapsibleSection({ title, content, defaultOpen = false, markdownComponents }) {
  const [open, setOpen] = useState(defaultOpen);
  // Larger, teal-accented section headers + colored left border so the
  // practitioner can scan vertically to the section they need
  // (Dr. Crowder directive 2026-04-23 16:56 PDT: bigger/different-color
  // section headers, clinical content is what matters most).
  return (
    <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden border-l-4" style={{ borderLeftColor: '#01696F' }} data-testid="collapsible-section">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-teal-50 transition-colors text-left" style={{ backgroundColor: open ? '#F0F9F9' : '#F7FAFA' }}>
        <span className="font-bold text-lg" style={{ color: '#01696F' }}>{title}</span>
        {open ? <ChevronUp className="h-5 w-5" style={{ color: '#01696F' }} /> : <ChevronDown className="h-5 w-5" style={{ color: '#01696F' }} />}
      </button>
      {open && (
        <div className="px-5 py-4">
          <article className="prose prose-base max-w-none prose-headings:text-gray-900 prose-p:text-gray-800 prose-p:leading-relaxed prose-strong:text-gray-900 prose-a:text-teal-600 prose-li:text-gray-800">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{content}</ReactMarkdown>
          </article>
        </div>
      )}
    </div>
  );
}

/* ──────── Main ProtocolView ──────── */
const ProtocolView = ({ generatedProtocol, assessment, setCurrentView, currentUser }) => {
  const [loading, setLoading] = useState(false);
  const [patientFileCreated, setPatientFileCreated] = useState(false);
  const [protocolQuestions, setProtocolQuestions] = useState([]);
  const [protocolQuestionLoading, setProtocolQuestionLoading] = useState(false);
  const [patientSummary, setPatientSummary] = useState(null);
  const [showPatientSummary, setShowPatientSummary] = useState(false);
  const [generatingPatientSummary, setGeneratingPatientSummary] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackText, setFeedbackText] = useState('');
  const [showQA, setShowQA] = useState(false);
  const [showFullDetail, setShowFullDetail] = useState(false);
  const [showSendToPatient, setShowSendToPatient] = useState(false);
  const [patientPhone, setPatientPhone] = useState('');
  const [sendingToPatient, setSendingToPatient] = useState(false);
  const protocolQuestionInputRef = useRef(null);

  if (!generatedProtocol) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-center" data-testid="protocol-view-empty">
        <h2 className="text-2xl font-bold text-gray-600 mb-4">No Protocol Generated</h2>
        <p className="text-gray-500">Please complete the assessment first to generate your personalized protocol.</p>
        <Button onClick={() => setCurrentView('assessment')} className="mt-4" data-testid="start-assessment-btn">Start Assessment</Button>
      </div>
    );
  }

  const clinicalDocument = (() => {
    const text = generatedProtocol?.clinical_reasoning || generatedProtocol?.ai_analysis || "";
    return typeof text === 'string' ? text : String(text);
  })();

  const glance = extractAtAGlance(generatedProtocol, assessment);
  const sections = splitIntoSections(clinicalDocument);

  const downloadProtocolPDF = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/generate-protocol-pdf`, {
        protocol_data: generatedProtocol,
        patient_data: { name: assessment.patient_name, age: assessment.age, weight: assessment.weight, email: assessment.email, gender: assessment.gender, height: assessment.height, primary_concerns: assessment.primary_concerns, health_goals: assessment.health_goals, current_medications: assessment.current_medications, allergies: assessment.allergies }
      }, { responseType: 'blob', timeout: 60000 });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `protocol_${assessment.patient_name?.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Protocol PDF downloaded");
    } catch { toast.error('PDF generation failed'); }
    finally { setLoading(false); }
  };

  const askProtocolQuestion = (question) => {
    if (!question || protocolQuestionLoading) return;
    setProtocolQuestions(prev => [...prev, { role: 'user', content: question }]);
    setProtocolQuestionLoading(true);
    const systemMsg = `The practitioner is asking about a specific generated protocol. Here is the full protocol content: ${clinicalDocument.substring(0, 3000)}\n\nAnswer questions about this specific protocol. Reference specific sections, peptides, dosing, and recommendations from this protocol in your responses.`;
    axios.post(`${API}/dr-peptide/chat`, { message: `${systemMsg}\n\nPRACTITIONER QUESTION: ${question}`, conversation_history: protocolQuestions.slice(-4).map(m => ({ role: m.role, content: m.content })) })
    .then(response => { if (response.data.success) setProtocolQuestions(prev => [...prev, { role: 'assistant', content: response.data.response }]); })
    .catch(() => setProtocolQuestions(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error. Please try again.' }]))
    .finally(() => setProtocolQuestionLoading(false));
  };

  const generatePatientSummary = async () => {
    setGeneratingPatientSummary(true);
    try {
      // Build patient summary from ACTUAL protocol data — NOT a separate LLM call
      const g = glance;
      const patientName = g.patient.name || 'Patient';
      const concerns = g.concerns.join(', ') || 'health optimization';
      
      let summary = `## What We're Addressing\n\n`;
      summary += `${patientName}, your provider has identified the following concerns: **${concerns}**. `;
      summary += `This 90-day protocol is designed specifically for you.\n\n`;
      
      summary += `## Your Treatment Approach\n\n`;
      summary += `Your provider has selected the following peptides for your protocol:\n\n`;
      g.peptides.forEach(p => {
        summary += `- **${p.name}**${p.dose ? `: ${p.dose}` : ''}${p.route ? ` (${p.route})` : ''}${p.cost ? ` — ${p.cost}` : ''}\n`;
      });
      
      summary += `\n## What to Expect\n\n`;
      summary += `- **Weeks 1-2:** Your body adjusts to the protocol. Mild side effects (if any) typically resolve.\n`;
      summary += `- **Weeks 3-4:** Early improvements in energy and appetite may become noticeable.\n`;
      summary += `- **Weeks 6-8:** Significant clinical improvements expected.\n`;
      summary += `- **Week 12:** Full protocol assessment. Your provider will evaluate progress and adjust.\n\n`;
      
      summary += `## Your Schedule\n\n`;
      g.peptides.forEach(p => {
        const isOral = /oral|capsule/i.test(p.route);
        summary += `- **${p.name}:** ${isOral ? 'Take daily as directed' : `${p.frequency || 'As prescribed'}`}\n`;
      });
      
      if (g.totalCost) {
        summary += `\n## Investment\n\nEstimated monthly cost: **${g.totalCost}**\n\n`;
      }
      
      summary += `You're taking a great step by working with your provider on this protocol. Stay consistent with the schedule, track how you feel each week, and remember that progress happens gradually. You've got this.\n`;
      
      setPatientSummary({ summary, timestamp: new Date().toLocaleString() });
      setShowPatientSummary(true);
    } catch { toast.error('Error generating summary'); }
    finally { setGeneratingPatientSummary(false); }
  };

  const runDeepResearch = async () => {
    const query = assessment.primary_concerns?.join(', ') || generatedProtocol?.recommended_peptides?.[0] || 'peptide therapy';
    setProtocolQuestionLoading(true);
    setShowQA(true);
    try {
      const response = await axios.post(`${API}/dr-peptide/deep-research`, { query, max_results: 10, days_back: 730 }, { timeout: 60000 });
      if (response.data.success) {
        const results = response.data.results;
        let text = `Deep Research Results (${results.total_results} studies found)\n\n`;
        if (results.pubmed?.length > 0) { text += `PubMed Studies:\n`; results.pubmed.slice(0, 5).forEach((s, i) => { text += `${i+1}. ${s.authors} (${s.year}) - "${s.title}"\n   ${s.journal} | PMID: ${s.pmid}\n\n`; }); }
        if (results.trials?.length > 0) { text += `\nClinical Trials:\n`; results.trials.forEach((t, i) => { text += `${i+1}. ${t.title}\n   Status: ${t.status} | Phase: ${t.phase}\n\n`; }); }
        setProtocolQuestions(prev => [...prev, { role: 'assistant', content: text }]);
      }
    } catch { toast.error('Deep research unavailable'); }
    finally { setProtocolQuestionLoading(false); }
  };

  const getPractitionerCode = () => currentUser?.id;

  const markdownComponents = {
    a: ({href, children}) => {
      const pmidMatch = href && href.match(/pubmed\.ncbi\.nlm\.nih\.gov\/(\d+)/);
      return <a href={href} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-800 underline font-medium">{children}{pmidMatch ? ` (PMID: ${pmidMatch[1]})` : ''}</a>;
    },
    // Bigger, more visible section hierarchy so a practitioner can scan
    // to the section they need (Dr. Crowder directive 2026-04-23 16:56 PDT).
    // H1 = document title, H2 = top-level protocol section (Clinical
    // Analysis, Peptide Selection, etc.), H3 = subsection inside a section.
    h1: ({children}) => <h1 className="text-2xl font-bold text-gray-900 mt-8 mb-3 pb-2 border-b-4" style={{borderColor: '#01696F'}}>{children}</h1>,
    h2: ({children}) => <h2 className="text-xl font-bold mt-6 mb-3 pb-2 border-b-2" style={{color: '#01696F', borderColor: '#B8D8DA'}}>{children}</h2>,
    h3: ({children}) => <h3 className="text-lg font-semibold mt-4 mb-2" style={{color: '#0C4E54'}}>{children}</h3>,
    h4: ({children}) => <h4 className="text-base font-semibold text-gray-800 mt-3 mb-1">{children}</h4>,
    hr: () => <hr className="my-4 border-gray-200" />,
    ul: ({children}) => <ul className="list-disc pl-5 space-y-1 my-2">{children}</ul>,
    ol: ({children}) => <ol className="list-decimal pl-5 space-y-1 my-2">{children}</ol>,
    li: ({children}) => <li className="text-gray-700 text-sm leading-relaxed">{children}</li>,
    p: ({children}) => <p className="text-gray-700 text-sm leading-relaxed mb-2">{children}</p>,
    strong: ({children}) => <strong className="font-bold text-gray-900">{children}</strong>,
    em: ({children}) => <em className="italic text-gray-600">{children}</em>,
    blockquote: ({children}) => <blockquote className="border-l-4 border-teal-400 pl-4 py-1 my-2 bg-teal-50 rounded-r">{children}</blockquote>,
    code: ({children, className}) => {
      if (className) return <pre className="bg-gray-50 border rounded p-3 my-2 overflow-x-auto text-xs text-gray-700">{children}</pre>;
      return <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono text-teal-800">{children}</code>;
    },
    table: ({children}) => <div className="overflow-x-auto my-3 rounded-lg border border-gray-200 shadow-sm"><table className="min-w-full text-sm border-collapse" data-testid="protocol-table">{children}</table></div>,
    thead: ({children}) => <thead className="bg-teal-600 text-white">{children}</thead>,
    tbody: ({children}) => <tbody className="divide-y divide-gray-100">{children}</tbody>,
    tr: ({children}) => <tr className="hover:bg-gray-50 transition-colors">{children}</tr>,
    th: ({children}) => <th className="px-3 py-2 text-left font-semibold text-white text-xs uppercase tracking-wide">{children}</th>,
    td: ({children}) => <td className="px-3 py-2 text-gray-700 text-sm border-t border-gray-100">{children}</td>,
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="protocol-view">
      {/* Header Bar */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-gray-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/images/dr_peptide_thumbs_up.png" alt="Dr. Peptide" className="w-10 h-10 rounded-lg shadow" onError={(e) => { e.target.style.display = 'none'; }} />
            <div>
              <h1 className="text-lg font-bold text-gray-900" data-testid="protocol-title">Personalized Protocol — {assessment.patient_name || 'Patient'}</h1>
              <p className="text-xs text-gray-500">Generated by Dr. Peptide AI &middot; {new Date().toLocaleDateString()}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={downloadProtocolPDF} disabled={loading} size="sm" className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="download-pdf-btn">
              {loading ? <><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1.5" /> PDF...</> : <><Download className="h-3.5 w-3.5 mr-1.5" /> Download PDF</>}
            </Button>
            <Button variant="outline" size="sm" onClick={() => window.print()} data-testid="print-btn"><Printer className="h-3.5 w-3.5 mr-1.5" /> Print</Button>
          </div>
        </div>
      </div>

      {/* ══════ PROTOCOL AT A GLANCE ══════ */}
      <div className="px-6 py-6 print:px-0" data-testid="protocol-at-a-glance">
        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200 rounded-xl p-5 space-y-4">
          <h2 className="text-lg font-bold text-teal-800 flex items-center gap-2">
            <Activity className="h-5 w-5" /> Protocol at a Glance
          </h2>

          {/* Patient Info Row */}
          <div className="flex flex-wrap gap-3 text-sm text-gray-700">
            <span><strong>Patient:</strong> {glance.patient.name}</span>
            {glance.patient.age && <span>| {glance.patient.age} {glance.patient.sex}</span>}
            {glance.patient.weight && <span>| {glance.patient.weight}</span>}
            {glance.patient.height && <span>| {glance.patient.height}</span>}
          </div>
          {glance.concerns.length > 0 && (
            <div className="text-sm"><strong className="text-gray-700">Chief Concerns:</strong> <span className="text-gray-600">{glance.concerns.join(', ')}</span></div>
          )}
          <div className="text-sm text-gray-600"><strong>Protocol Duration:</strong> 90 Days</div>

          {/* Recommended Peptide Stack */}
          <div className="space-y-2">
            <h3 className="text-sm font-bold text-teal-700 flex items-center gap-1.5"><Pill className="h-4 w-4" /> Recommended Peptide Stack</h3>
            <div className="grid gap-1.5">
              {glance.peptides.map((p, i) => (
                <div key={i} className="flex items-center justify-between bg-white/70 rounded-lg px-3 py-2 text-sm" data-testid={`glance-peptide-${i}`}>
                  <div>
                    <span className="font-semibold text-teal-700">{p.name}</span>
                    {p.dose && <span className="text-gray-500 ml-2">{p.dose}</span>}
                    {p.route && <span className="text-gray-400 ml-1">({p.route})</span>}
                  </div>
                  {p.cost && <span className="text-xs text-gray-400">{p.cost}</span>}
                </div>
              ))}
            </div>
            {glance.totalCost && (
              <div className="text-sm flex items-center gap-1.5 text-gray-600">
                <DollarSign className="h-3.5 w-3.5" /> <strong>Est. Monthly Cost:</strong> {glance.totalCost}
              </div>
            )}
          </div>

          {/* Drug Interactions */}
          <div className="flex items-center gap-2 text-sm">
            <ShieldAlert className="h-4 w-4 text-amber-600" />
            <strong className="text-gray-700">Drug Interactions:</strong>
            <span className="text-gray-600">{glance.interactionCount} checked</span>
            {glance.interactionCount === 0 && <span className="text-green-600 text-xs font-medium">No critical interactions found</span>}
          </div>

          {/* Predicted Outcomes */}
          {glance.successRate && (
            <div className="text-sm text-gray-600"><strong>Success Rate:</strong> {glance.successRate}</div>
          )}
        </div>

        {/* Dosing Calendar */}
        <DosingCalendar peptides={glance.peptides} clinicalReasoning={clinicalDocument} />

        {/* Allergy Safety Note (B5) */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 my-4 flex gap-3" data-testid="allergy-safety-note">
          <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-amber-800 mb-1">Important Safety Note</p>
            <p className="text-xs text-amber-700 leading-relaxed">Before starting any new medication, please confirm with your provider that you do not have known allergies to any of the peptides or compounds listed above. If you experience any unexpected reaction (rash, swelling, difficulty breathing, or unusual symptoms), contact your provider immediately or seek emergency medical attention.</p>
          </div>
        </div>
      </div>

      {/* ══════ FULL CLINICAL DETAIL (Collapsible) ══════ */}
      <div className="px-6 pb-4 print:px-0" data-testid="protocol-full-detail">
        <button
          onClick={() => setShowFullDetail(!showFullDetail)}
          className="w-full flex items-center justify-between px-4 py-3 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors mb-3"
          data-testid="toggle-full-detail"
        >
          <span className="font-bold text-sm text-slate-700">Full Clinical Detail ({sections.length} sections)</span>
          {showFullDetail ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>

        {showFullDetail && (
          <div className="space-y-1">
            {sections.map((s, i) => (
              <CollapsibleSection key={i} title={s.title || `Section ${i + 1}`} content={s.content} defaultOpen={i === 0} markdownComponents={markdownComponents} />
            ))}
          </div>
        )}

        {/* Medical Disclaimer */}
        <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded text-center" data-testid="protocol-medical-disclaimer">
          <p className="text-xs text-slate-500">PeptideProtocols.ai is a clinical decision support tool. All protocols must be reviewed by the prescribing practitioner before implementation. This is not medical advice.</p>
        </div>
      </div>

      {/* ══════ ACTION BAR ══════ */}
      <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-gray-200 px-3 md:px-6 py-2 md:py-3 print:hidden z-40" data-testid="protocol-action-bar">
        <div className="flex items-center gap-2 flex-nowrap overflow-x-auto pb-1">
          <Button size="sm" onClick={downloadProtocolPDF} disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="download-pdf-btn-bottom">
            <Download className="h-3.5 w-3.5 mr-1" /> {loading ? 'Generating...' : 'Download PDF'}
          </Button>
          {!patientFileCreated ? (
            <Button size="sm" onClick={async () => {
              try {
                setLoading(true);
                const code = getPractitionerCode();
                await axios.post(`${API}/patients`, { practitioner_code: code, patient_data: assessment, assessment, protocol: generatedProtocol }, { headers: getAuthHeaders() });
                setPatientFileCreated(true);
                toast.success(`Patient file created for ${assessment.patient_name || 'Patient'}`);
              } catch { toast.error('Error creating patient file'); }
              finally { setLoading(false); }
            }} disabled={loading} className="bg-green-600 hover:bg-green-700 text-white" data-testid="create-patient-file-btn">
              <FolderPlus className="h-3.5 w-3.5 mr-1" /> Create Patient File
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setCurrentView('dashboard')} className="border-green-300 text-green-700" data-testid="view-patient-file-btn">
              <User className="h-3.5 w-3.5 mr-1" /> View Patient File
            </Button>
          )}
          <div className="h-5 w-px bg-gray-300 mx-1" />
          <Button variant="outline" size="sm" onClick={() => { setShowQA(!showQA); if (!showQA) setTimeout(() => protocolQuestionInputRef.current?.focus(), 100); }} data-testid="toggle-qa-btn">
            <MessageCircle className="h-3.5 w-3.5 mr-1" /> Ask Question
          </Button>
          <Button variant="outline" size="sm" onClick={runDeepResearch} disabled={protocolQuestionLoading} data-testid="protocol-deep-research-btn">
            <Search className="h-3.5 w-3.5 mr-1" /> Deep Research
          </Button>
          <Button variant="outline" size="sm" disabled={generatingPatientSummary} onClick={generatePatientSummary} data-testid="protocol-patient-summary-btn">
            <FileText className="h-3.5 w-3.5 mr-1" /> {generatingPatientSummary ? 'Generating...' : 'Patient Summary'}
          </Button>
          <Button variant="outline" size="sm" onClick={async () => {
            try { await navigator.clipboard.writeText(clinicalDocument); toast.success("Protocol copied to clipboard"); } catch { toast.error("Copy failed"); }
          }} data-testid="copy-protocol-btn"><Copy className="h-3.5 w-3.5 mr-1" /> Copy</Button>
          <Button variant="outline" size="sm" onClick={() => setShowSendToPatient(true)} data-testid="send-to-patient-btn"><Share2 className="h-3.5 w-3.5 mr-1" /> Send to Patient</Button>
          <Button variant="outline" size="sm" onClick={() => { setFeedbackText(''); setShowFeedbackModal(true); }} data-testid="feedback-btn">Feedback</Button>
          <div className="h-5 w-px bg-gray-300 mx-1" />
          <Button variant="outline" size="sm" onClick={() => setCurrentView('chat')} data-testid="go-to-chat-btn"><MessageCircle className="h-3.5 w-3.5 mr-1" /> Chat</Button>
          <Button variant="outline" size="sm" onClick={() => setCurrentView('assessment')} data-testid="new-assessment-btn"><User className="h-3.5 w-3.5 mr-1" /> New Assessment</Button>
        </div>
      </div>

      {/* ══════ Q&A Slide-out Panel ══════ */}
      {showQA && ReactDOM.createPortal(
        <div style={{position:'fixed', bottom:'80px', right:'16px', width:'420px', maxHeight:'55vh', zIndex:9999}} className="bg-white rounded-xl shadow-2xl border border-gray-200 flex flex-col print:hidden" data-testid="protocol-qa-section">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-teal-50 rounded-t-xl">
            <h3 className="font-semibold text-teal-800 text-sm flex items-center gap-2"><MessageCircle className="h-4 w-4" /> Ask About This Protocol</h3>
            <button onClick={() => setShowQA(false)} className="text-gray-400 hover:text-gray-600"><ChevronUp className="h-4 w-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[100px] max-h-[40vh]">
            {protocolQuestions.length === 0 && <div className="text-center text-gray-400 text-sm py-4">Ask any question about this protocol</div>}
            {protocolQuestions.map((msg, index) => (
              <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] p-3 rounded-lg text-sm ${msg.role === 'user' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-800'}`}>
                  {msg.role === 'assistant' ? (
                    <div className="prose prose-sm max-w-none text-gray-800">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ ...markdownComponents, p: ({children}) => <p className="text-xs leading-relaxed mb-1">{children}</p>, li: ({children}) => <li className="text-xs leading-relaxed">{children}</li> }}>{typeof msg.content === 'string' ? msg.content : String(msg.content)}</ReactMarkdown>
                    </div>
                  ) : <div className="text-sm whitespace-pre-wrap">{msg.content}</div>}
                </div>
              </div>
            ))}
            {protocolQuestionLoading && (
              <div className="flex justify-start"><div className="bg-gray-100 p-3 rounded-lg flex items-center gap-2 text-sm"><div className="animate-spin rounded-full h-3 w-3 border-b-2 border-teal-600" /> Analyzing...</div></div>
            )}
          </div>
          <div className="border-t p-3">
            <div className="flex gap-2">
              <input ref={protocolQuestionInputRef} type="text" data-testid="protocol-question-input"
                onKeyDown={(e) => { if (e.key === 'Enter' && !protocolQuestionLoading) { const q = protocolQuestionInputRef.current?.value?.trim(); if (q) { protocolQuestionInputRef.current.value = ''; askProtocolQuestion(q); } } }}
                placeholder="Ask a question..." className="flex-1 p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm" disabled={protocolQuestionLoading} />
              <Button data-testid="ask-protocol-question-btn" size="sm" onClick={() => { const q = protocolQuestionInputRef.current?.value?.trim(); if (q) { protocolQuestionInputRef.current.value = ''; askProtocolQuestion(q); } }} disabled={protocolQuestionLoading}>
                {protocolQuestionLoading ? '...' : 'Ask'}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Patient Summary Modal */}
      {showPatientSummary && patientSummary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:hidden">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-2xl font-bold text-green-800">Patient-Friendly Summary</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowPatientSummary(false)}>X</Button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              <div className="prose prose-sm max-w-none text-gray-800 leading-relaxed">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                  {typeof patientSummary.summary === 'string' ? patientSummary.summary : String(patientSummary.summary)}
                </ReactMarkdown>
              </div>
              <div className="mt-6 flex flex-col items-center gap-2">
                <img src="/images/dr_peptide_thumbs_up.png" alt="Dr. Peptide" style={{width: '80px', height: '80px', objectFit: 'contain'}} onError={(e) => { e.target.style.display = 'none'; }} />
                <p className="text-sm font-medium" style={{color: '#20808D'}}>You've got this!</p>
              </div>
              <div className="mt-4 text-xs text-gray-500">Generated: {patientSummary.timestamp}</div>
            </div>
            <div className="p-6 border-t bg-gray-50 flex gap-3">
              <Button className="flex-1" onClick={() => { const pw = window.open('', '_blank'); pw.document.write(`<html><head><title>Patient Summary</title><style>body{font-family:Arial,sans-serif;max-width:800px;margin:20px auto;padding:20px;line-height:1.6}h1{color:#166534;border-bottom:2px solid #166534;padding-bottom:10px}</style></head><body><h1>Patient Summary</h1><div style="white-space:pre-wrap">${patientSummary.summary}</div><div style="margin-top:40px;padding-top:20px;border-top:1px solid #ccc;font-size:12px;color:#666"><p>Generated by PeptideProtocols.ai - ${patientSummary.timestamp}</p></div></body></html>`); pw.document.close(); pw.print(); }}>Print Summary</Button>
              <Button variant="outline" onClick={async () => { try { await navigator.clipboard.writeText(patientSummary.summary); toast.success('Copied'); } catch {} }}>Copy</Button>
              <Button variant="outline" onClick={() => setShowPatientSummary(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:hidden">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-bold mb-2">Protocol Feedback</h2>
            <p className="text-sm text-gray-500 mb-4">Help improve future protocols with your clinical observations.</p>
            <textarea value={feedbackText} onChange={(e) => setFeedbackText(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm min-h-[100px] focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              placeholder="Accuracy, completeness, clinical suggestions..." autoFocus />
            <div className="flex gap-2 pt-3">
              <Button className="flex-1" disabled={!feedbackText.trim()} onClick={async () => {
                try { await axios.post(`${API}/dr-peptide/feedback`, { message: clinicalDocument.substring(0, 500), feedback: feedbackText.trim(), type: "protocol_feedback", timestamp: new Date().toISOString() }); toast.success("Feedback submitted!"); setShowFeedbackModal(false); } catch { toast.error("Could not submit feedback"); }
              }}>Submit Feedback</Button>
              <Button variant="outline" onClick={() => setShowFeedbackModal(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Send to Patient Modal */}
      {showSendToPatient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:hidden">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-bold mb-2">Send to Patient</h2>
            <p className="text-sm text-gray-500 mb-4">Send the patient-friendly summary via text message or generate a shareable link.</p>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Patient Phone Number</label>
                <input type="tel" value={patientPhone} onChange={(e) => setPatientPhone(e.target.value)}
                  placeholder="(555) 123-4567" className="w-full mt-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500" data-testid="patient-phone-input" />
              </div>
              <Button className="w-full" disabled={!patientPhone.trim() || sendingToPatient} data-testid="send-sms-btn"
                onClick={async () => {
                  setSendingToPatient(true);
                  try {
                    await axios.post(`${API}/protocol/send-to-patient`, {
                      phone: patientPhone, patient_name: assessment.patient_name || 'Patient',
                      summary: `Protocol Summary for ${assessment.patient_name || 'Patient'}:\n${glance.peptides.map(p => `- ${p.name}: ${p.dose}`).join('\n')}\nEst. Cost: ${glance.totalCost}\n\nGenerated by PeptideProtocols.ai`
                    });
                    toast.success("Summary sent to patient!");
                    setShowSendToPatient(false); setPatientPhone('');
                  } catch { toast.error("Could not send. Please try again."); }
                  finally { setSendingToPatient(false); }
                }}>
                {sendingToPatient ? 'Sending...' : 'Send via SMS'}
              </Button>
              <div className="text-center text-xs text-gray-400">— or —</div>
              <Button variant="outline" className="w-full" data-testid="copy-share-link-btn"
                onClick={async () => {
                  const shareUrl = `${window.location.origin}/protocol/${generatedProtocol.protocol_id || 'shared'}`;
                  try { await navigator.clipboard.writeText(shareUrl); toast.success("Shareable link copied!"); } catch { toast.error("Could not copy link"); }
                }}>
                Copy Shareable Link
              </Button>
            </div>
            <div className="flex justify-end mt-4">
              <Button variant="outline" onClick={() => setShowSendToPatient(false)}>Close</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProtocolViewWithErrorBoundary = (props) => (
  <ProtocolErrorBoundary setCurrentView={props.setCurrentView}>
    <ProtocolView {...props} />
  </ProtocolErrorBoundary>
);

export default ProtocolViewWithErrorBoundary;
